import React from 'react';
import { motion } from 'framer-motion';
import type { SceneSection } from '../manifest';
import type { StylePack } from '../style-packs';
import { RoyalGoldSmokeText, RoyalGoldCrestBadge } from '../interactive/RoyalGoldSmokeText';

interface PichwaiPresentationSectionProps {
  section: SceneSection;
  pack: StylePack;
  showCard: boolean;
  setShowCard: (show: boolean) => void;
}

const RAJPUT_PHERE_VOWS = [
  { step: 1, title: 'Pratham Vachan', desc: 'To honor dharma and provide nourishing sustenance for our family.' },
  { step: 2, title: 'Dwitiya Vachan', desc: 'To stand as a fortress of strength and unwavering devotion in all times.' },
  { step: 3, title: 'Tritiya Vachan', desc: 'To earn noble wealth through righteousness and shared endeavor.' },
  { step: 4, title: 'Chaturth Vachan', desc: 'To bring harmony, joy, and spiritual wisdom into our sacred home.' },
  { step: 5, title: 'Pancham Vachan', desc: 'To cherish our children and nurture them with noble family heritage.' },
  { step: 6, title: 'Shashtha Vachan', desc: 'To protect health, peace, and longevity across all seasons of life.' },
  { step: 7, title: 'Saptam Vachan', desc: 'To remain true friends and united souls across seven lifetimes.' },
];

export function PichwaiPresentationSection({
  section,
  pack,
  showCard,
  setShowCard,
}: PichwaiPresentationSectionProps) {
  const moment =
    section.momentId ??
    (section.order === 0
      ? 'engagement'
      : section.order === 1
      ? 'haldi'
      : section.order === 2
      ? 'antarpat'
      : section.order === 3
      ? 'varmala'
      : 'saptapadi');

  // Translucent royal obsidian & crimson glass — leaves palace & ceremony video completely visible
  const baseCardStyle: React.CSSProperties = {
    backgroundColor: 'rgba(24, 10, 14, 0.52)',
    backdropFilter: 'blur(20px) contrast(115%)',
    WebkitBackdropFilter: 'blur(20px) contrast(115%)',
    border: '1.5px solid rgba(212, 175, 55, 0.45)',
    boxShadow: '0 28px 75px rgba(0, 0, 0, 0.85), inset 0 0 30px rgba(212, 175, 55, 0.15)',
    color: '#FFFBEB',
    transform: 'translateZ(0)',
    willChange: 'transform',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: pack.fonts.display,
    color: '#FFFFFF',
    textShadow: '0 2px 20px rgba(0,0,0,0.95), 0 0 35px rgba(212, 175, 55, 0.65)',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: pack.fonts.script ?? pack.fonts.display,
    color: '#FDE68A',
    textShadow: '0 2px 14px rgba(0,0,0,0.9)',
  };

  const goldPillStyle =
    'bg-stone-950/60 border border-amber-400/40 text-amber-100 font-serif backdrop-blur-xl shadow-[0_4px_20px_rgba(212,175,55,0.25)]';

  const toggleButton = (
    <button
      onClick={() => setShowCard(!showCard)}
      className="absolute -top-3.5 right-4 z-40 flex items-center gap-1.5 rounded-full border border-amber-300/50 bg-stone-950/90 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-amber-300 shadow-xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 cursor-pointer pointer-events-auto"
      title="Toggle pure royal artwork showcase"
    >
      <span>{showCard ? '👁️ ROYAL SCENE' : '📜 CEREMONY SCROLL'}</span>
    </button>
  );

  return (
    <div className="relative z-10 w-full min-h-screen flex pointer-events-none">
      <style>{`
        @keyframes royal-gold-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .royal-gold-shimmer-active {
          background: linear-gradient(110deg, #FFFFFF 0%, #FFFBEB 20%, #FDE047 45%, #D4AF37 70%, #996515 90%, #FFFFFF 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: royal-gold-shimmer 5s ease-in-out infinite;
        }
      `}</style>

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT I: SAGAI DASTOOR — Left-Docked Royal Jharokha Arch with Gold Mist Text Blur Reveal
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'engagement' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-start px-6 md:px-14 py-12">
          <div
            className={`relative max-w-md md:max-w-lg w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            {/* Mewar Rajput Royal Arch Crest — Unblurs from gold smoke */}
            <div className="mb-3">
              <RoyalGoldCrestBadge delay={0.05} icon="⚜️">
                ACT I · SAGAI DASTOOR
              </RoyalGoldCrestBadge>
            </div>

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-3xl relative">
              {/* Smoky Gold-Dust Calligraphic Title Reveal */}
              <RoyalGoldSmokeText
                as="h2"
                delay={0.15}
                duration={1.05}
                className="text-2xl md:text-4xl font-bold leading-tight mb-2"
                style={titleStyle}
              >
                {section.content.title}
              </RoyalGoldSmokeText>

              {section.content.subtitle && (
                <RoyalGoldSmokeText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-lg md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </RoyalGoldSmokeText>
              )}

              {/* Staggered Gold Leaf Detail Badges */}
              <div className="mt-4 flex flex-col gap-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">👑</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Shubh Mahurat Date</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">☀️</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Godhuli Mahurat Time</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.time || '05:30 PM Sunset'}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">🏛️</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Royal Durbar Venue</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.venue?.name || 'Zenana Mahal Courtyard, Udaipur'}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT II: PITHHI DASTOOR — Imperial Pavilion Bar with Turmeric-Mist Blur Dissolve
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'haldi' && (
        <div className="w-full flex items-end justify-center pb-8 md:pb-12 px-4 md:px-10">
          <div
            className={`relative max-w-5xl w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div
              style={baseCardStyle}
              className="p-6 md:p-8 rounded-3xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center shadow-2xl relative overflow-hidden"
            >
              <div className="md:border-r border-amber-500/30 md:pr-6">
                <RoyalGoldCrestBadge delay={0.05} icon="🏵️" className="mb-2">
                  ACT II · PITHHI DASTOOR
                </RoyalGoldCrestBadge>
                <RoyalGoldSmokeText
                  as="h2"
                  delay={0.15}
                  duration={1.0}
                  className="text-2xl md:text-3xl font-bold leading-tight"
                  style={titleStyle}
                >
                  {section.content.title}
                </RoyalGoldSmokeText>
                {section.content.subtitle && (
                  <RoyalGoldSmokeText
                    as="p"
                    delay={0.3}
                    duration={0.85}
                    className="text-base md:text-lg mt-1 font-semibold"
                    style={subtitleStyle}
                  >
                    {section.content.subtitle}
                  </RoyalGoldSmokeText>
                )}
              </div>

              <div className="space-y-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-lg">🌿</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Sacred Pithhi Ubtan</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                    <p className="text-[11px] text-amber-200 font-medium">{section.content.time || '10:00 AM Morning Mahurat'}</p>
                  </div>
                </motion.div>
              </div>

              <div className="space-y-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-lg">🦚</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Royal Pichwai Garden</p>
                    <p className="text-xs md:text-sm font-bold text-white line-clamp-1">{section.content.venue?.name || 'Mor Chowk Palace Lawn'}</p>
                    <p className="text-[11px] text-amber-200 font-medium">Attire: Traditional Rajput Poshaak</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT III: SHUBH ANTARPAT — Vertical Royal Decree Scroll with Gold Mist Unblur
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'antarpat' && (
        <div className="w-full flex items-center justify-center px-6 py-12">
          <div
            className={`relative max-w-2xl w-full pointer-events-auto transition-opacity duration-300 text-center ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div style={baseCardStyle} className="p-8 md:p-11 rounded-3xl relative">
              <div className="mb-3">
                <RoyalGoldCrestBadge delay={0.05} icon="✦">
                  ACT III · SHUBH ANTARPAT & GATHBANDHAN
                </RoyalGoldCrestBadge>
              </div>

              <RoyalGoldSmokeText
                as="h2"
                delay={0.15}
                duration={1.05}
                className="text-3xl md:text-5xl font-bold leading-tight mb-3"
                style={titleStyle}
              >
                {section.content.title}
              </RoyalGoldSmokeText>

              {section.content.subtitle && (
                <RoyalGoldSmokeText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-xl md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </RoyalGoldSmokeText>
              )}

              <RoyalGoldSmokeText
                as="p"
                delay={0.45}
                duration={0.8}
                className="text-xs md:text-sm text-amber-100/90 max-w-lg mx-auto mb-6 leading-relaxed"
              >
                As the sacred verses chant through the Mewar palace mandap, the consecrated silk cloth is lowered and the eternal knot is tied.
              </RoyalGoldSmokeText>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.55 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${goldPillStyle}`}
                >
                  📅 {section.content.date}
                </motion.span>
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.65 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${goldPillStyle}`}
                >
                  ⏰ {section.content.time || '06:15 PM Vivah Mahurat'}
                </motion.span>
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.75 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${goldPillStyle}`}
                >
                  📍 {section.content.venue?.name || 'Mewar Royal Mandap'}
                </motion.span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT IV: TORAN & VARMALA — Right-Docked Palace Balcony Panel
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'varmala' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-end px-6 md:px-14 py-12">
          <div
            className={`relative max-w-md md:max-w-lg w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div className="mb-3">
              <RoyalGoldCrestBadge delay={0.05} icon="🌺">
                ACT IV · TORAN & VARMALA
              </RoyalGoldCrestBadge>
            </div>

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-3xl relative">
              <RoyalGoldSmokeText
                as="h2"
                delay={0.15}
                duration={1.0}
                className="text-2xl md:text-4xl font-bold leading-tight mb-2"
                style={titleStyle}
              >
                {section.content.title}
              </RoyalGoldSmokeText>

              {section.content.subtitle && (
                <RoyalGoldSmokeText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-lg md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </RoyalGoldSmokeText>
              )}

              <div className="mt-4 flex flex-col gap-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.45 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">🪷</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Lotus Varmala Garland</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.55 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">⚔️</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Toran Swagat Mahurat</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.time || '07:15 PM Evening'}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.65 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${goldPillStyle}`}
                >
                  <span className="text-xl">🏰</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Palace Gateway</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.venue?.name || 'Hathi Pol Toran Dwar'}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT V: SACRED SAAT PHERE — Royal Mandala Arch Scroll with Seven Sacred Vows
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'saptapadi' && (
        <div className="w-full flex items-end justify-center pb-6 md:pb-10 px-4 md:px-8">
          <div
            className={`relative max-w-6xl w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div style={baseCardStyle} className="p-6 md:p-8 rounded-3xl shadow-2xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4 pb-3 border-b border-amber-500/25">
                <div>
                  <div className="mb-1">
                    <RoyalGoldCrestBadge delay={0.05} icon="🔥">
                      SACRED SAPTAPADI · SEVEN SACRED VOWS
                    </RoyalGoldCrestBadge>
                  </div>
                  <RoyalGoldSmokeText
                    as="h2"
                    delay={0.15}
                    duration={1.0}
                    className="text-2xl md:text-3xl font-bold"
                    style={titleStyle}
                  >
                    {section.content.title}
                  </RoyalGoldSmokeText>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${goldPillStyle}`}>
                    📅 {section.content.date}
                  </span>
                  <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${goldPillStyle}`}>
                    ⏰ {section.content.time || '08:45 PM Sacred Phere'}
                  </span>
                </div>
              </div>

              {/* 7 Rajput Sacred Vows with Sequential Gold Mist Unblur */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
                {RAJPUT_PHERE_VOWS.map((vow, i) => (
                  <motion.div
                    key={vow.step}
                    initial={{ filter: 'blur(18px) drop-shadow(0 0 25px #D4AF37)', opacity: 0, scale: 0.9 }}
                    whileInView={{ filter: 'blur(0px) drop-shadow(0 0 10px rgba(212,175,55,0.4))', opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, delay: 0.2 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="p-3 rounded-2xl bg-stone-950/80 border border-amber-400/40 text-left backdrop-blur-xl shadow-lg hover:border-amber-400/80 hover:scale-105 transition-all"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-black text-amber-300">STEP {vow.step}</span>
                      <span className="text-xs">🏵️</span>
                    </div>
                    <p className="text-[11px] font-bold text-amber-100 line-clamp-1">{vow.title}</p>
                    <p className="text-[9px] text-amber-200/90 mt-0.5 line-clamp-2 leading-relaxed">{vow.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
